import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { useSearchParams } from 'react-router-dom';
import { SearchIcon } from '@/components/catalog/icons';
import { Notice } from '@/components/ui/Notice';
import { TextLink } from '@/components/ui/TextLink';
import { ViewToggle } from '@/components/ui/ViewToggle';
import {
  Badge,
  Button,
  Checkbox,
  EmptyState,
  Field,
  Input,
  ListPager,
  MultiCombobox,
  PageHeader,
  Panel,
  PanelHeader,
  Skeleton,
  Table,
  TableWrap,
  TBody,
  TD,
  TH,
  THead,
  TR,
} from '@/components/ui';
import { api, ApiError } from '@/lib/api';
import { formatDateOnly, formatDateTime } from '@/lib/format';
import {
  BUCKET_LABELS,
  LAST_CONTACT_KIND_LABELS,
  bucketTone,
} from '@/lib/stage-status-follow-up';
import { taskStateLabel } from '@/lib/task-time';
import type {
  CompanyStageStatus,
  ProspectFollowUpBucket,
  ProspectFollowUpListParams,
  ProspectFollowUpRow,
  ProspectFollowUpSummary,
  ProspectFollowUpSummaryParams,
} from '@/lib/types';
import { useAuth } from '@/lib/use-auth';
import { useCompanyStageStatuses } from '@/lib/use-company-stage-statuses';
import { fetchAllPages, useShowAll } from '@/lib/use-show-all';
import { useUserOptions } from '@/lib/use-user-options';

/**
 * Prospect Follow-ups (owner 2026-10-23) — `/prospect-follow-ups`.
 *
 * One row per Prospect company the follow-up rules apply to, bucketed by the
 * SERVER (`GET /prospect-follow-ups`): overdue / due soon / on track for an SLA
 * sub-stage, resurfacing / waiting for a resurface sub-stage, and no sub-stage.
 * This page never recomputes a bucket, a due instant or a day count — it renders
 * what the server says, so the report and the SLA notifications cannot disagree.
 *
 * ## Gates
 *
 * The route carries no guard. Reading needs `trackers-prospect-follow-ups` (the
 * view control) AND `companies` (the server's real guard — rows are companies,
 * ownership-scoped). Either missing renders the refusal.
 *
 * ## Everything the list is scoped by lives in the URL
 *
 * `?bucket=&mine=1&accountLeadId=&stageStatusId=&search=&page=`. Every default
 * is the key's ABSENCE and nothing is written on arrival: no `bucket` is the
 * Actionable view (overdue + due soon + on track + resurfacing), no `page` is
 * page 1. Any filter change drops `page`. `mine` and `accountLeadId` are
 * exclusive — setting one clears the other. `accountLeadId` and
 * `stageStatusId` are multi-select (owner 2026-10-28): a CSV of ids in the same
 * key, sent as CSV.
 *
 * ⚠️ The Sub-stage options are the statuses of every stage that has at least
 * one status with follow-up behaviour (an SLA or a resurface mode) — found by
 * FLAGS, never by the stage's name — minus the `excludesFollowUp` statuses,
 * which the server leaves out of the population.
 */

const PER_PAGE = 25;
const SHOW_ALL_KEY = 'prospect-follow-ups';
const SEARCH_DEBOUNCE_MS = 250;

/** `actionable` is the absent `bucket` key, never written to the URL. */
type BucketView = 'actionable' | ProspectFollowUpBucket;

const BUCKET_ORDER: readonly ProspectFollowUpBucket[] = [
  'overdue',
  'due_soon',
  'resurfacing',
  'on_track',
  'no_sub_stage',
  'waiting',
];

function parseBucket(raw: string | null): BucketView {
  return raw !== null && (BUCKET_ORDER as readonly string[]).includes(raw)
    ? (raw as ProspectFollowUpBucket)
    : 'actionable';
}

function parsePositiveInt(raw: string | null): number | null {
  if (raw === null) return null;
  const parsed = Number(raw);
  return Number.isSafeInteger(parsed) && parsed > 0 ? parsed : null;
}

/**
 * A multi-value id key (owner 2026-10-28): the valid ids, de-duplicated and
 * comma-joined, or `''` for none. A STRING so memo dependencies compare by
 * value; an old single-id bookmark parses as a one-item list. Malformed parts
 * are dropped — one would 422 the whole list.
 */
function parseIdCsv(raw: string | null): string {
  if (raw === null) return '';
  const ids: number[] = [];
  for (const part of raw.split(',')) {
    const id = parsePositiveInt(part.trim() === '' ? null : part.trim());
    if (id !== null && !ids.includes(id)) ids.push(id);
  }
  return ids.join(',');
}

function idsOf(csv: string): number[] {
  return csv === '' ? [] : csv.split(',').map(Number);
}

function summaryCount(
  summary: ProspectFollowUpSummary | null,
  view: BucketView,
): number | null {
  if (summary === null) return null;
  switch (view) {
    case 'actionable':
      return summary.overdue + summary.dueSoon + summary.onTrack + summary.resurfacing;
    case 'overdue':
      return summary.overdue;
    case 'due_soon':
      return summary.dueSoon;
    case 'on_track':
      return summary.onTrack;
    case 'resurfacing':
      return summary.resurfacing;
    case 'waiting':
      return summary.waiting;
    case 'no_sub_stage':
      return summary.noSubStage;
  }
}

/** A true instant (dueAt, lastContact.at, task dueAt), localised by `formatDateTime`. */
function formatInstant(iso: string | null): string {
  if (!iso) return '—';
  if (Number.isNaN(new Date(iso).getTime())) return '—';
  return formatDateTime(iso);
}

function errorMessage(cause: unknown): string {
  if (cause instanceof ApiError) return cause.message;
  if (cause instanceof Error) return cause.message;
  return 'The follow-ups didn’t load. Try again.';
}

// ── Cells (one copy, shared by the table and the stacked cards) ─────────────

function CompanyCell({ row }: { row: ProspectFollowUpRow }) {
  return (
    <TextLink to={`/companies/${row.company.id}`} className="font-medium">
      {row.company.name.trim() === '' ? `Company #${row.company.id}` : row.company.name}
    </TextLink>
  );
}

function SubStageCell({ row }: { row: ProspectFollowUpRow }) {
  return (
    <div className="flex flex-wrap items-center gap-1.5">
      <span className={row.stageStatus ? 'text-fg' : 'text-fg-muted'}>
        {row.stageStatus?.name ?? '—'}
      </span>
      <Badge tone={bucketTone(row.bucket)}>{BUCKET_LABELS[row.bucket]}</Badge>
    </div>
  );
}

function AccountLeadCell({ row }: { row: ProspectFollowUpRow }) {
  return <span className={row.accountLead ? undefined : 'text-fg-muted'}>{row.accountLead?.name ?? '—'}</span>;
}

function LastContactCell({ row }: { row: ProspectFollowUpRow }) {
  if (!row.lastContact) return <span className="text-fg-muted">—</span>;
  return (
    <span className="whitespace-nowrap">
      <span className="text-fg-secondary">{LAST_CONTACT_KIND_LABELS[row.lastContact.kind]}</span>
      <span className="text-fg-muted"> · </span>
      <span className="tabular">{formatInstant(row.lastContact.at)}</span>
    </span>
  );
}

function DueCell({ row }: { row: ProspectFollowUpRow }) {
  const resurface = row.bucket === 'resurfacing' || row.bucket === 'waiting';
  if (resurface) {
    return <span className="tabular whitespace-nowrap">{formatDateOnly(row.followUpOn)}</span>;
  }
  if (row.dueAt === null) return <span className="text-fg-muted">—</span>;
  const overdue = row.daysOverdue !== null && row.daysOverdue > 0;
  return (
    <div className="flex flex-wrap items-center gap-1.5">
      <span className="tabular whitespace-nowrap">{formatInstant(row.dueAt)}</span>
      {overdue && (
        <Badge tone="danger">
          {row.daysOverdue === 1 ? '1 day overdue' : `${row.daysOverdue} days overdue`}
        </Badge>
      )}
    </div>
  );
}

function TaskCell({ row }: { row: ProspectFollowUpRow }) {
  const task = row.followUpTask;
  if (!task) return <span className="text-fg-muted">—</span>;
  return (
    <span className="whitespace-nowrap">
      <span className="text-fg-secondary">{taskStateLabel(task.state)}</span>
      {task.dueAt && (
        <>
          <span className="text-fg-muted"> · </span>
          <span className="tabular">{formatInstant(task.dueAt)}</span>
        </>
      )}
    </span>
  );
}

const COLUMNS: readonly { key: string; label: string; Cell: (props: { row: ProspectFollowUpRow }) => ReactNode }[] = [
  { key: 'company', label: 'Company', Cell: CompanyCell },
  { key: 'subStage', label: 'Sub-stage', Cell: SubStageCell },
  { key: 'accountLead', label: 'Account lead', Cell: AccountLeadCell },
  { key: 'lastContact', label: 'Last contact', Cell: LastContactCell },
  { key: 'due', label: 'Due', Cell: DueCell },
  { key: 'task', label: 'Follow-up task', Cell: TaskCell },
];

function TableSkeleton() {
  return (
    <TBody>
      {Array.from({ length: 6 }, (_, index) => (
        <TR key={index}>
          {COLUMNS.map((column) => (
            <TD key={column.key}>
              <Skeleton className="h-3 w-24" />
            </TD>
          ))}
        </TR>
      ))}
    </TBody>
  );
}

function CardSkeleton() {
  return (
    <ul aria-busy className="m-0 list-none p-0">
      {Array.from({ length: 4 }, (_, index) => (
        <li key={index} className="grid gap-2 border-b border-border px-4 py-3 last:border-b-0">
          <Skeleton className="h-3.5 w-40" />
          <Skeleton className="h-3 w-56" />
          <Skeleton className="h-3 w-32" />
        </li>
      ))}
    </ul>
  );
}

// ── Page ─────────────────────────────────────────────────────────────────────

export function ProspectFollowUpsPage() {
  const { ability } = useAuth();
  const canRead = ability.canRead('trackers-prospect-follow-ups') && ability.canRead('companies');

  const [searchParams, setSearchParams] = useSearchParams();
  const searchKey = searchParams.toString();

  const bucket = parseBucket(searchParams.get('bucket'));
  const mine = searchParams.get('mine') === '1';
  /* CSV strings (see `parseIdCsv`); `''` = no filter. */
  const accountLeadId = mine ? '' : parseIdCsv(searchParams.get('accountLeadId'));
  const stageStatusId = parseIdCsv(searchParams.get('stageStatusId'));
  const search = searchParams.get('search') ?? '';
  const page = parsePositiveInt(searchParams.get('page')) ?? 1;

  /** ⚠️ `{ replace: true }` on EVERY write — a filter change is not a navigation. */
  function writeParams(mutate: (params: URLSearchParams) => void, keepPage = false) {
    const params = new URLSearchParams(searchKey);
    mutate(params);
    if (!keepPage) params.delete('page');
    setSearchParams(params, { replace: true });
  }

  // ── Search, debounced into the URL ─────────────────────────────────────────
  const [searchDraft, setSearchDraft] = useState(search);
  const [lastUrlSearch, setLastUrlSearch] = useState(search);
  if (search !== lastUrlSearch) {
    // The URL moved under us (back/forward, a link): the box follows it.
    setLastUrlSearch(search);
    setSearchDraft(search);
  }
  useEffect(() => {
    const trimmed = searchDraft.trim();
    if (trimmed === search) return;
    const timer = window.setTimeout(() => {
      const params = new URLSearchParams(searchKey);
      if (trimmed === '') params.delete('search');
      else params.set('search', trimmed);
      params.delete('page');
      setLastUrlSearch(trimmed);
      setSearchParams(params, { replace: true });
    }, SEARCH_DEBOUNCE_MS);
    return () => window.clearTimeout(timer);
  }, [searchDraft, search, searchKey, setSearchParams]);

  // ── Pickers ─────────────────────────────────────────────────────────────────
  const staff = useUserOptions(canRead, true);
  const leadOptions = useMemo(() => {
    const options = staff.byGroup.account_lead.users.map((user) => ({
      value: String(user.id),
      label: user.name,
      hint: user.email,
    }));
    for (const held of idsOf(accountLeadId)) {
      if (!options.some((option) => option.value === String(held))) {
        options.unshift({ value: String(held), label: `User #${held}`, hint: '' });
      }
    }
    return options;
  }, [staff.byGroup, accountLeadId]);

  const stageStatuses = useCompanyStageStatuses(canRead);
  const subStageOptions = useMemo<CompanyStageStatus[]>(() => {
    const followUpStages = new Set(
      stageStatuses.statuses
        .filter((status) => status.followUpSlaDays != null || status.resurfaceMode != null)
        .map((status) => status.stage.id),
    );
    const held = new Set(idsOf(stageStatusId));
    return stageStatuses.statuses.filter(
      (status) =>
        followUpStages.has(status.stage.id) &&
        status.excludesFollowUp !== true &&
        (status.isActive || held.has(status.id)),
    );
  }, [stageStatuses.statuses, stageStatusId]);

  const subStagePickerOptions = useMemo(() => {
    const options = subStageOptions.map((status) => ({ value: String(status.id), label: status.name }));
    for (const held of idsOf(stageStatusId)) {
      if (!options.some((option) => option.value === String(held))) {
        options.unshift({ value: String(held), label: `Sub-stage #${held}` });
      }
    }
    return options;
  }, [subStageOptions, stageStatusId]);

  // ── Summary (the pill counts) ───────────────────────────────────────────────
  /** The list's filters minus bucket and paging, so the pill counts match the rows. */
  const summaryParams = useMemo<ProspectFollowUpSummaryParams>(() => {
    const params: ProspectFollowUpSummaryParams = {};
    if (mine) params.mine = true;
    if (accountLeadId !== '') params.accountLeadId = accountLeadId;
    if (stageStatusId !== '') params.stageStatusId = stageStatusId;
    if (search !== '') params.search = search;
    return params;
  }, [mine, accountLeadId, stageStatusId, search]);

  const [summary, setSummary] = useState<ProspectFollowUpSummary | null>(null);
  const [summaryFailed, setSummaryFailed] = useState(false);
  useEffect(() => {
    if (!canRead) return;
    const controller = new AbortController();
    api
      .getProspectFollowUpSummary(summaryParams, controller.signal)
      .then((response) => {
        if (controller.signal.aborted) return;
        setSummary(response.data);
        setSummaryFailed(false);
      })
      .catch(() => {
        if (controller.signal.aborted) return;
        setSummary(null);
        setSummaryFailed(true);
      });
    return () => controller.abort();
  }, [canRead, summaryParams]);

  // ── Rows ─────────────────────────────────────────────────────────────────────
  const [showAll, setShowAll] = useShowAll(SHOW_ALL_KEY);
  const [rows, setRows] = useState<ProspectFollowUpRow[] | null>(null);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [truncated, setTruncated] = useState(false);
  const [reloadToken, setReloadToken] = useState(0);
  /*
   * The request a settled answer belongs to. `loading` is derived — the current
   * request's key differs from the last settled one — so the effect never sets
   * state synchronously.
   */
  const [settled, setSettled] = useState<{ key: string; error: string | null } | null>(null);

  const listParams = useMemo<ProspectFollowUpListParams>(() => {
    const params: ProspectFollowUpListParams = {};
    if (bucket !== 'actionable') params.bucket = bucket;
    if (mine) params.mine = true;
    if (accountLeadId !== '') params.accountLeadId = accountLeadId;
    if (stageStatusId !== '') params.stageStatusId = stageStatusId;
    if (search !== '') params.search = search;
    return params;
  }, [bucket, mine, accountLeadId, stageStatusId, search]);

  const requestKey = JSON.stringify([listParams, showAll ? 'all' : page, reloadToken]);
  const loading = settled?.key !== requestKey;
  const error = settled?.key === requestKey ? settled.error : null;

  useEffect(() => {
    if (!canRead) return;
    const controller = new AbortController();
    const { signal } = controller;
    const request = showAll
      ? fetchAllPages(
          (pageNo, perPage, pageSignal) =>
            api.listProspectFollowUps({ ...listParams, page: pageNo, perPage }, pageSignal),
          signal,
        ).then((all) => ({
          data: all.rows,
          total: all.total,
          totalPages: 1,
          truncated: all.truncated,
        }))
      : api
          .listProspectFollowUps({ ...listParams, page, perPage: PER_PAGE }, signal)
          .then((result) => ({
            data: result.data,
            total: result.meta.total,
            totalPages: result.meta.totalPages,
            truncated: false,
          }));
    request
      .then((result) => {
        if (signal.aborted) return;
        setRows(result.data);
        setTotal(result.total);
        setTotalPages(result.totalPages);
        setTruncated(result.truncated);
        setSettled({ key: requestKey, error: null });
      })
      .catch((cause: unknown) => {
        if (signal.aborted) return;
        setSettled({ key: requestKey, error: errorMessage(cause) });
      });
    return () => controller.abort();
  }, [canRead, listParams, page, showAll, requestKey]);

  // ── Writers ─────────────────────────────────────────────────────────────────
  function setBucket(next: BucketView) {
    writeParams((params) => {
      if (next === 'actionable') params.delete('bucket');
      else params.set('bucket', next);
    });
  }

  function setMine(next: boolean) {
    writeParams((params) => {
      if (next) {
        params.set('mine', '1');
        params.delete('accountLeadId');
      } else {
        params.delete('mine');
      }
    });
  }

  function setAccountLead(next: string) {
    writeParams((params) => {
      if (next === '') params.delete('accountLeadId');
      else {
        params.set('accountLeadId', next);
        params.delete('mine');
      }
    });
  }

  function setStageStatus(next: string) {
    writeParams((params) => {
      if (next === '') params.delete('stageStatusId');
      else params.set('stageStatusId', next);
    });
  }

  function setPage(next: number) {
    writeParams((params) => {
      if (next <= 1) params.delete('page');
      else params.set('page', String(next));
    }, true);
  }

  function clearFilters() {
    setSearchDraft('');
    writeParams((params) => {
      params.delete('mine');
      params.delete('accountLeadId');
      params.delete('stageStatusId');
      params.delete('search');
    });
  }

  const bucketOptions = useMemo(
    () =>
      (['actionable', ...BUCKET_ORDER] as const).map((value) => {
        const label = value === 'actionable' ? 'Actionable' : BUCKET_LABELS[value];
        const count = summaryCount(summary, value);
        return {
          value: value as BucketView,
          label: (
            <span className="inline-flex items-center gap-1.5">
              {label}
              {count !== null && <span className="tabular text-fg-muted">{count}</span>}
            </span>
          ),
        };
      }),
    [summary],
  );

  // Below every hook; the route carries no guard.
  if (!canRead) {
    return (
      <>
        <PageHeader title="Prospect Follow-ups" />
        <Panel className="min-w-0">
          <EmptyState
            title="This page isn’t available to your account"
            description="Your permission set does not include this. An administrator can change that."
          />
        </Panel>
      </>
    );
  }

  const filtersActive = mine || accountLeadId !== '' || stageStatusId !== '' || search !== '';
  const initialLoad = rows === null && loading;
  const visibleRows = rows ?? [];
  const firstRow = total === 0 ? 0 : showAll ? 1 : (page - 1) * PER_PAGE + 1;
  const lastRow = total === 0 ? 0 : firstRow + visibleRows.length - 1;

  const emptyState = filtersActive ? (
    <EmptyState
      title="No companies match these filters"
      description="Clear the filters to see every follow-up in this view."
      action={
        <Button variant="secondary" size="sm" onClick={clearFilters}>
          Clear filters
        </Button>
      }
    />
  ) : (
    <EmptyState
      title="Nothing to follow up."
      description={
        bucket === 'actionable'
          ? 'Give Prospect companies a sub-stage on the Companies page to start their follow-up clock.'
          : 'Pick another view above to see the rest of the follow-ups.'
      }
    />
  );

  return (
    <>
      <PageHeader title="Prospect Follow-ups" />

      <Panel aria-labelledby="prospect-follow-ups-heading" className="min-w-0">
        <PanelHeader
          id="prospect-follow-ups-heading"
          className="[&_h2]:font-medium [&_h2]:text-fg-muted"
          title={bucket === 'actionable' ? 'Actionable' : BUCKET_LABELS[bucket]}
        />

        <div className="border-b border-border px-4 py-3">
          <ViewToggle
            label="Follow-up view"
            value={bucket}
            options={bucketOptions}
            onChange={setBucket}
          />
        </div>

        <div className="flex flex-wrap items-end gap-x-3 gap-y-3 border-b border-border px-4 py-3">
          <div className="w-full min-w-0 sm:w-64">
            <Field label="Search">
              {({ id, 'aria-describedby': describedBy }) => (
                <Input
                  id={id}
                  aria-describedby={describedBy}
                  type="search"
                  leadingIcon={<SearchIcon />}
                  value={searchDraft}
                  onChange={(event) => setSearchDraft(event.target.value)}
                />
              )}
            </Field>
          </div>

          <div className="w-full min-w-0 sm:w-56">
            <Field label="Account lead">
              {({ id, 'aria-describedby': describedBy }) => (
                <MultiCombobox
                  id={id}
                  aria-describedby={describedBy}
                  values={idsOf(accountLeadId).map(String)}
                  onChange={(values) => setAccountLead(values.join(','))}
                  options={leadOptions}
                  summaryNoun={{ singular: 'account lead', plural: 'account leads' }}
                  placeholder="Anyone"
                  searchPlaceholder="Search people…"
                  loading={staff.loading}
                />
              )}
            </Field>
          </div>

          <div className="w-full min-w-0 sm:w-56">
            <Field label="Sub-stage">
              {({ id, 'aria-describedby': describedBy }) => (
                <MultiCombobox
                  id={id}
                  aria-describedby={describedBy}
                  values={idsOf(stageStatusId).map(String)}
                  onChange={(values) => setStageStatus(values.join(','))}
                  options={subStagePickerOptions}
                  summaryNoun={{ singular: 'sub-stage', plural: 'sub-stages' }}
                  placeholder="Any sub-stage"
                  searchPlaceholder="Search sub-stages…"
                  loading={stageStatuses.loading}
                />
              )}
            </Field>
          </div>

          <div className="flex h-9 items-center max-sm:h-11">
            <Checkbox
              label="Mine"
              checked={mine}
              onChange={(event) => setMine(event.target.checked)}
            />
          </div>
        </div>

        {staff.failed && (
          <Notice tone="danger" role="alert" className="mx-4 mt-3" title="Couldn’t load the account leads.">
            The account lead filter is empty because the list didn’t load. Reload to try again.
          </Notice>
        )}
        {stageStatuses.error !== null && (
          <Notice tone="danger" role="alert" className="mx-4 mt-3" title="Couldn’t load the sub-stages.">
            {stageStatuses.error}
          </Notice>
        )}
        {summaryFailed && (
          <Notice tone="danger" role="alert" className="mx-4 mt-3" title="Couldn’t load the counts.">
            The view counts didn’t load. The list below is unaffected.
          </Notice>
        )}
        {error !== null && (
          <Notice
            tone="danger"
            role="alert"
            className="mx-4 mt-3"
            title="Couldn’t load the follow-ups."
            action={
              <Button variant="secondary" size="sm" onClick={() => setReloadToken((value) => value + 1)}>
                Try again
              </Button>
            }
          >
            <span className="[overflow-wrap:anywhere]">{error}</span>
          </Notice>
        )}

        <div
          aria-busy={loading || undefined}
          className={
            loading && rows !== null
              ? 'opacity-60 transition-opacity duration-150 ease-out motion-reduce:transition-none'
              : undefined
          }
        >
          {/* Below md the rows become a stacked list with the same columns. */}
          <div className="md:hidden">
            {initialLoad ? (
              <CardSkeleton />
            ) : visibleRows.length === 0 ? (
              error === null && emptyState
            ) : (
              <ul className="m-0 list-none p-0">
                {visibleRows.map((row) => (
                  <li key={row.key} className="border-b border-border px-4 py-3 last:border-b-0">
                    <CompanyCell row={row} />
                    <dl className="mt-2.5 grid gap-1.5 text-xs">
                      {COLUMNS.filter((column) => column.key !== 'company').map((column) => (
                        <div key={column.key} className="flex items-baseline gap-2">
                          <dt className="w-24 shrink-0 text-fg-subtle">{column.label}</dt>
                          <dd className="m-0 min-w-0 flex-1">
                            <column.Cell row={row} />
                          </dd>
                        </div>
                      ))}
                    </dl>
                  </li>
                ))}
              </ul>
            )}
          </div>

          <div className="hidden md:block">
            {!initialLoad && visibleRows.length === 0 ? (
              error === null && emptyState
            ) : (
              <TableWrap frozenHeader>
                <Table style={{ minWidth: 960 }}>
                  <THead>
                    <TR>
                      {COLUMNS.map((column) => (
                        <TH key={column.key}>{column.label}</TH>
                      ))}
                    </TR>
                  </THead>
                  {initialLoad ? (
                    <TableSkeleton />
                  ) : (
                    <TBody>
                      {visibleRows.map((row) => (
                        <TR key={row.key}>
                          {COLUMNS.map((column) => (
                            <TD key={column.key}>
                              <column.Cell row={row} />
                            </TD>
                          ))}
                        </TR>
                      ))}
                    </TBody>
                  )}
                </Table>
              </TableWrap>
            )}
          </div>
        </div>

        {rows !== null && (
          <ListPager
            page={showAll ? 1 : page}
            totalPages={showAll ? 1 : totalPages}
            total={total}
            firstRow={firstRow}
            lastRow={lastRow}
            perPage={PER_PAGE}
            showAll={showAll}
            loading={loading}
            onPage={setPage}
            onToggleShowAll={setShowAll}
            noun="companies"
            truncated={showAll && truncated}
          />
        )}
      </Panel>
    </>
  );
}

export default ProspectFollowUpsPage;
